// Browser-geolocation primitives shared by the flows that offer a carrier a
// "Use current location" shortcut next to a typed city (Find Me Freight, Request
// a Rental). Stateless on purpose — each modal owns its own loading/error state
// and its own abandoned-after-close semantics, which differ. What lives here is
// the part that was genuinely identical: the timeouts, the promise wrapper, and
// the error taxonomy.

/**
 * GPS can hang indefinitely (indoors, browser glitches) — race the fix against
 * a timeout so the button never spins forever.
 *
 * 30s, not the 10s mobile uses: a desktop browser's FIRST fix of a session has
 * to wake the OS location service and do a Wi-Fi-based lookup, which regularly
 * runs 15-30s cold (and is near-instant after). VendorMarketplace passes no
 * timeout at all, so it waits out that cold start and looks reliable by
 * comparison — 10s was cutting off fixes that were still on their way.
 */
export const LOCATION_TIMEOUT_MS = 30000;

/**
 * A fix from the last few minutes is plenty precise for a city, and reusing one
 * skips the cold-start wait entirely.
 */
export const LOCATION_MAX_AGE_MS = 300000;

/** Promise wrapper over the callback-style geolocation API, with our timeouts. */
export const getCurrentPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      maximumAge: LOCATION_MAX_AGE_MS,
      timeout: LOCATION_TIMEOUT_MS,
    });
  });

/** One variant per spec'd GeolocationPositionError code. */
export type LocationError = 'denied' | 'timeout' | 'unavailable' | null;

/**
 * Classify a geolocation rejection.
 *
 * Duck-types the code rather than using `instanceof GeolocationPositionError`:
 * that constructor isn't exposed in every browser (or in jsdom), so the
 * instanceof check silently mislabeled real permission denials as generic
 * failures. Codes are the spec'd 1/2/3.
 */
export const classifyLocationError = (
  error: unknown
): Exclude<LocationError, null> => {
  const code = (error as { code?: number })?.code;
  if (code === 1) return 'denied';
  if (code === 3) return 'timeout';
  return 'unavailable';
};

/**
 * Each failure gets copy that says what to actually do about it — "denied" is a
 * browser setting the user can flip, "unavailable" is usually the OS
 * withholding location from the browser, "timeout" is worth just retrying. All
 * three are recoverable by typing a city instead, so none of them render as a
 * hard error.
 */
export const LOCATION_ERROR_MESSAGES: Record<
  Exclude<LocationError, null>,
  string
> = {
  denied:
    'Location access is blocked for this site. Allow it in your browser settings, or just type your city or ZIP below.',
  timeout: 'That took too long. Try again, or type your city or ZIP below.',
  unavailable:
    "Your device wouldn't share a location. Check that location services are on for your browser, or type your city or ZIP below.",
};

/**
 * Testid SUFFIX per error. Each flow prefixes with its own namespace rather
 * than sharing one id — two modals emitting `requestLoad:permissionDenied`
 * would make a failing selector ambiguous about which flow broke.
 */
const LOCATION_ERROR_TEST_IDS: Record<Exclude<LocationError, null>, string> = {
  denied: 'permissionDenied',
  timeout: 'locationTimeout',
  unavailable: 'locationFailed',
};

export const locationErrorTestId = (
  prefix: string,
  error: Exclude<LocationError, null>
): string => `${prefix}:${LOCATION_ERROR_TEST_IDS[error]}`;
